/**
 * The two identities the ClawInstall property can be branded as.
 *
 * @since 1.0.0
 */
export type BrandIdentityId = "clawinstall" | "consinna";

/**
 * A brand identity for the ClawInstall property, resolved by request host.
 *
 * Both identities are the same legal entity (Consinna LLC) and the same product;
 * only the front-door name, canonical host, and copyright line differ by host.
 *
 * @since 1.0.0
 */
export interface BrandIdentity {
  readonly id: BrandIdentityId;
  /** Brand name shown in the UI, page titles, og:site_name, JSON-LD. */
  readonly displayName: string;
  /** Registered legal entity — the same LLC behind both identities. */
  readonly legalName: string;
  /** Trade name the entity does business as on this host; null when the host carries the entity's own name. */
  readonly dbaName: string | null;
  /** Entity sentence for legal prose. */
  readonly legalLine: string;
  /** Text after "© <year> " in the footer. */
  readonly copyrightHolder: string;
  /** Canonical public host for canonical/og:url/sitemap/robots. */
  readonly canonicalHost: string;
  /** Origin hosting the authenticated surfaces (/login, /dashboard, /onboarding); null = this host. */
  readonly authOrigin: string | null;
  /** Single letter for the squared brand-mark badge (a white "C" on a dark rounded square). */
  readonly monogram: string;
  /** Logo asset path for structured data / OG. The shared "C" monogram mark serves BOTH identities. */
  readonly logoSrc: string;
  readonly faviconHref: string;
  readonly emails: {
    readonly support: string;
    readonly privacy: string;
    readonly legal: string;
    readonly security: string;
    readonly abuse: string;
    readonly billing: string;
  };
}

/** Shared mailboxes for the ClawInstall identity. */
const CLAWINSTALL_EMAILS = {
  support: "support@clawinstall.ai",
  privacy: "privacy@clawinstall.ai",
  legal: "legal@clawinstall.ai",
  security: "security@clawinstall.ai",
  abuse: "abuse@clawinstall.ai",
  billing: "billing@clawinstall.ai",
} as const;

/**
 * Consinna mailboxes: only `support` follows the identity (`@consinna.ai`); the
 * rest reuse the `@clawinstall.ai` groups until `@consinna.ai` groups exist.
 */
const CONSINNA_EMAILS = { ...CLAWINSTALL_EMAILS, support: "support@consinna.ai" } as const;

/**
 * Hosts of the ClawInstall property that render the Consinna identity. Spread
 * into {@link DOMAIN_ROUTE_MAP} so routing and identity cannot drift.
 *
 * Declared above `DOMAIN_ROUTE_MAP` because it is spread into it — placing it
 * lower throws a `ReferenceError` at module load (and breaks `server.ts` boot).
 *
 * @since 1.0.0
 */
export const CONSINNA_HOSTS: ReadonlyArray<string> = ["consinna.ai", "www.consinna.ai"];

/**
 * Domain-to-route-prefix mapping for multi-domain routing
 *
 * Maps external hostnames to internal route prefixes used by TanStack Router's
 * URL rewrite system. When a request arrives from one of these domains,
 * the input rewrite prepends the corresponding prefix to the pathname.
 *
 * @since 1.0.0
 */
export const DOMAIN_ROUTE_MAP: Record<string, string> = {
  "clawinstall.ai": "/clawinstall",
  "www.clawinstall.ai": "/clawinstall",
  "setupmyagent.ai": "/clawinstall",
  "installmyagent.ai": "/clawinstall",
  // Consinna is a second front door to the same product on the same route
  // prefix; spread here so a host can never route without also having an
  // identity (see `identityForHostname`).
  ...Object.fromEntries(CONSINNA_HOSTS.map((host) => [host, "/clawinstall"] as const)),
  "clawinstall.test": "/clawinstall",
  // Dev ngrok hostname — fronts localhost:5173 over public HTTPS so the
  // OAuth callback URL Google sees is reachable from the internet. Same
  // route-prefix as clawinstall.test (it's a dev surface, not prod).
  "clawinstall.ngrok.dev": "/clawinstall",
  "orca.theinfinityguides.com": "/orca",
  "orca.test": "/orca",
  // Dev ngrok hostname — fronts localhost:5173 over public HTTPS so Linear /
  // GitHub webhooks reach the API via the web `/api/*` proxy. Same route
  // prefix as orca.test (it's a dev surface, not prod).
  "orca.ngrok.io": "/orca",
  // orcaloops.ai — the production orca portal (apex + www; every other
  // single-label subdomain resolves via the `.orcaloops.ai` suffix rule).
  "orcaloops.ai": "/orca",
  "www.orcaloops.ai": "/orca",
  "flirtmachine.app": "/flirtmachine",
  "www.flirtmachine.app": "/flirtmachine",
  "fm.test": "/flirtmachine",
  "localhost": "/flirtmachine",
};

/**
 * Route-prefix-to-domain mapping for URL output rewriting
 *
 * Reverse mapping used by the output rewrite to strip internal prefixes
 * and set the correct hostname for link generation. This ensures that
 * `<Link>` components generate clean URLs with the proper domain.
 *
 * @since 1.0.0
 */
export const ROUTE_PREFIX_DOMAIN_MAP: Record<string, string> = {
  "/clawinstall": "clawinstall.ai",
  "/orca": "orcaloops.ai",
  "/flirtmachine": "flirtmachine.app",
};

/**
 * Domains that should NOT be rewritten to the canonical production domain.
 * When a request arrives from one of these hosts, the output rewrite
 * preserves the original hostname instead of redirecting to production.
 *
 * @since 1.0.0
 */
export const LOCAL_DOMAINS: ReadonlySet<string> = new Set([
  "clawinstall.test",
  // Dev ngrok hostname — fronts localhost:5173 over public HTTPS for
  // OAuth callback testing. Treat as local so output rewriting doesn't
  // redirect generated links back to clawinstall.ai.
  "clawinstall.ngrok.dev",
  "fm.test",
  "orca.test",
  // Dev ngrok hostname — fronts localhost:5173 over public HTTPS for
  // Linear / GitHub webhook delivery. Treat as local so output rewriting
  // doesn't redirect generated links back to orca.theinfinityguides.com.
  "orca.ngrok.io",
  "localhost",
]);

/**
 * Known sub-paths for each route prefix.
 *
 * Only these paths get the prefix prepended during input rewriting.
 * All other paths (e.g. /admin, /auth/sign-in, /dashboard) pass through
 * unmodified to match against the main route tree.
 *
 * @since 1.0.0
 */
export const DOMAIN_ROUTE_PATHS: Record<string, ReadonlySet<string>> = {
  "/clawinstall": new Set([
    "/",
    "/acceptable-use",
    "/admin",
    "/compare",
    "/confirmation",
    "/dashboard",
    "/faq",
    "/login",
    "/logout",
    "/onboarding",
    "/pricing",
    "/privacy",
    // The whole reseller surface (dashboard, onboarding wizard, invite start
    // links, Stripe return URLs) lives under /reseller — without this entry
    // the public host never prefix-rewrites those paths and they 404
    // (found in the first reseller onboarding walkthrough, 2026-07-06).
    "/reseller",
    "/security",
    // The unlisted self-serve sign-up door (CLA-1032). One entry also covers
    // Clerk's `/sign-up/*` steps by the `/`-boundary rule in `longestPathMatch`.
    "/sign-up",
    "/terms",
  ]),
  "/flirtmachine": new Set([
    "/",
    "/admin",
    "/admin-panel",
    "/chat",
    "/dashboard",
    "/login",
    "/subscription",
  ]),
};

/**
 * Domain prefixes whose hostname is dedicated to a single sub-app, so EVERY
 * pathname on that hostname should be rewritten to live under the prefix.
 *
 * Used for hostnames like `orca.test` / `orca.theinfinityguides.com` where
 * the entire site is the sub-app and dynamic identifier paths (e.g.
 * `/INF-464`) cannot reasonably be enumerated in `DOMAIN_ROUTE_PATHS`.
 *
 * @since 1.0.0
 */
export const WILDCARD_DOMAIN_PREFIXES: ReadonlySet<string> = new Set([
  "/orca",
]);

/**
 * Top-level system paths that must remain reachable from every host
 * regardless of route-prefix rewriting.
 *
 * Without this carve-out, a wildcard prefix like `/orca` would rewrite
 * `orca.theinfinityguides.com/auth/sign-in` to `/orca/auth/sign-in` (no such
 * route) and a non-admin redirected `to="/access-denied"` would land on
 * `/orca/access-denied` (no such route). These routes live at the root of
 * the route tree and apply equally to every sub-app.
 *
 * Matched as a prefix with a `/` boundary, so `/auth` matches `/auth`,
 * `/auth/sign-in`, `/auth/sign-up/sso-callback`, etc., but not `/authority`.
 *
 * @since 1.0.0
 */
export const SHARED_NON_PREFIXED_PATHS: ReadonlySet<string> = new Set([
  "/auth",
  "/sso-callback",
  "/access-denied",
  // OAuth provider callback (routes/onboarding/oauth/callback/$provider):
  // a STATIC URL registered once per provider in their developer console.
  // On clawinstall hosts the enumerated "/onboarding" entry would otherwise
  // rewrite it under /clawinstall (no such route → 404), eating the
  // round-trip after the provider consent screen.
  "/onboarding/oauth",
]);

/**
 * Check if a `pathname` is one of the shared system paths that must bypass
 * route-prefix rewriting.
 *
 * @since 1.0.0
 */
export const isSharedNonPrefixedPath = (pathname: string): boolean => {
  for (const p of SHARED_NON_PREFIXED_PATHS) {
    if (pathname === p) return true;
    if (pathname.startsWith(p + "/")) return true;
  }
  return false;
};

/**
 * Check if a pathname belongs to a domain's route subtree
 *
 * For wildcard prefixes (single-app subdomains), every pathname matches
 * EXCEPT shared system paths (see `SHARED_NON_PREFIXED_PATHS`) which must
 * resolve at the route-tree root regardless of host.
 *
 * Otherwise, matches the pathname exactly, OR as a subpath of one of the
 * declared entry points (so `/admin` enables `/admin`, `/admin/onboardings`,
 * `/admin/onboardings/new`, etc.). The `/` root only matches exactly.
 *
 * @since 1.0.0
 */
/**
 * Length of the longest entry in `candidates` matching `pathname` (exactly,
 * or as a `/`-bounded prefix), or `-1` when nothing matches.
 *
 * @since 1.0.0
 */
const longestPathMatch = (candidates: Iterable<string>, pathname: string): number => {
  let best = -1;
  for (const p of candidates) {
    if (pathname === p || (p !== "/" && pathname.startsWith(p + "/"))) {
      if (p.length > best) best = p.length;
    }
  }
  return best;
};

export const isDomainPath = (prefix: string, pathname: string): boolean => {
  const normalized = pathname === "/" ? "/" : pathname.replace(/\/$/, "");
  // Precedence between a domain's enumerated entry points and the shared
  // root-level carve-outs is MOST-SPECIFIC-WINS: a brand's own enumerated
  // "/auth/sign-in" would beat the shared "/auth", while the shared
  // "/onboarding/oauth" beats clawinstall's "/onboarding" (the OAuth provider
  // callback must resolve at the route-tree root on every host).
  const sharedLen = longestPathMatch(SHARED_NON_PREFIXED_PATHS, normalized);
  if (WILDCARD_DOMAIN_PREFIXES.has(prefix)) {
    return sharedLen === -1;
  }
  const paths = DOMAIN_ROUTE_PATHS[prefix];
  if (!paths) return false;
  const domainLen = longestPathMatch(paths, normalized);
  return domainLen !== -1 && domainLen > sharedLen;
};

/**
 * Hostname suffix rules, applied only after an exact {@link DOMAIN_ROUTE_MAP}
 * miss. Two kinds of non-exact host resolve here: the per-brand preview apexes
 * (`<slug>.preview.<brand-apex>`) and the orcaloops.ai wildcard (every
 * single-label subdomain `<x>.orcaloops.ai` is the orca portal). The API-side
 * brand registry (`packages/api/src/config/brands.ts` `PREVIEW_SUFFIX_BRAND_RULES`)
 * is the mirror of this list for the host→brand mapping; keep the two lists in
 * lockstep when a suffix changes.
 *
 * `<slug>.preview.clawinstall.ai`   → `/clawinstall`
 * `<slug>.preview.flirtmachine.app` → `/flirtmachine`
 * `<x>.orcaloops.ai`                → `/orca`
 *
 * @since 1.0.0
 */
const PREVIEW_SUFFIX_ROUTE_RULES: ReadonlyArray<{ readonly suffix: string; readonly prefix: string }> = [
  { suffix: ".preview.clawinstall.ai", prefix: "/clawinstall" },
  { suffix: ".preview.flirtmachine.app", prefix: "/flirtmachine" },
  // Every single-label subdomain of orcaloops.ai is the orca portal.
  { suffix: ".orcaloops.ai", prefix: "/orca" },
];

/**
 * Resolve the route prefix for a given hostname
 *
 * Returns the internal route prefix for the hostname, or undefined
 * if the hostname maps to the default (main site) routes.
 *
 * Resolution order mirrors the API brand registry: the exact
 * {@link DOMAIN_ROUTE_MAP} first, then the {@link PREVIEW_SUFFIX_ROUTE_RULES}
 * for per-brand preview apexes, then `undefined` (default site).
 *
 * @since 1.0.0
 */
export const getRoutePrefixForHostname = (hostname: string): string | undefined => {
  const normalized = hostname.toLowerCase();
  const exact = DOMAIN_ROUTE_MAP[normalized];
  if (exact !== undefined) return exact;
  for (const rule of PREVIEW_SUFFIX_ROUTE_RULES) {
    if (normalized.endsWith(rule.suffix)) return rule.prefix;
  }
  return undefined;
};

/**
 * The two brand identities the ClawInstall property can render as, keyed by id.
 *
 * @since 1.0.0
 */
export const BRAND_IDENTITIES: Record<BrandIdentityId, BrandIdentity> = {
  clawinstall: {
    id: "clawinstall",
    displayName: "ClawInstall",
    legalName: "Consinna LLC",
    dbaName: "ClawInstall",
    legalLine: "Consinna LLC, a California limited liability company doing business as ClawInstall",
    copyrightHolder: "Consinna LLC, doing business as ClawInstall",
    canonicalHost: "clawinstall.ai",
    authOrigin: null,
    monogram: "C",
    logoSrc: "/clawinstall-logo.svg",
    faviconHref: "/clawinstall-favicon.svg",
    emails: CLAWINSTALL_EMAILS,
  },
  consinna: {
    id: "consinna",
    displayName: "Consinna",
    legalName: "Consinna LLC",
    dbaName: null,
    legalLine: "Consinna LLC, a California limited liability company",
    copyrightHolder: "Consinna LLC",
    canonicalHost: "consinna.ai",
    authOrigin: "https://clawinstall.ai",
    monogram: "C",
    logoSrc: "/clawinstall-logo.svg",
    faviconHref: "/clawinstall-favicon.svg",
    emails: CONSINNA_EMAILS,
  },
};

/**
 * Identity for a bare, lower-cased hostname; undefined for hosts outside the
 * ClawInstall property. Resolved through the ONE host registry: any host that
 * routes to `/clawinstall` gets an identity, and the {@link CONSINNA_HOSTS}
 * subset gets the Consinna identity.
 *
 * @since 1.0.0
 */
export const identityForHostname = (hostname: string): BrandIdentity | undefined => {
  const normalized = hostname.toLowerCase();
  if (getRoutePrefixForHostname(normalized) !== "/clawinstall") return undefined;
  return CONSINNA_HOSTS.includes(normalized) ? BRAND_IDENTITIES.consinna : BRAND_IDENTITIES.clawinstall;
};

/**
 * Resolve the domain for a given route prefix
 *
 * Returns the external domain for the route prefix, or undefined
 * if the prefix maps to the default (main site) domain.
 *
 * @since 1.0.0
 */
export const getDomainForRoutePrefix = (pathname: string): { domain: string; prefix: string } | undefined => {
  for (const [prefix, domain] of Object.entries(ROUTE_PREFIX_DOMAIN_MAP)) {
    if (pathname === prefix || pathname.startsWith(prefix + "/")) {
      return { domain, prefix };
    }
  }
  return undefined;
};

/**
 * Route prefixes that expose a brand `/login` route.
 *
 * Hosts that don't map to one of these (e.g. an unknown preview host) fall back
 * to the FlirtMachine login — the default application. The orca subdomain is a
 * wildcard-prefix host, so its `/login` rewrites to `/orca/login` (the route at
 * `routes/orca/login/`); it must be listed here so `resolveLoginPath` keeps an
 * unauthenticated orca visitor on a login that resolves on the orca host
 * instead of bouncing to `/flirtmachine/login` (a 404 under the orca wildcard).
 *
 * @since 1.0.0
 */
const LOGIN_ROUTE_PREFIXES: ReadonlySet<string> = new Set([
  "/clawinstall",
  "/flirtmachine",
  "/orca",
]);

/**
 * Resolve the brand-specific login path for a request host.
 *
 * This is the single source of truth for "where does an unauthenticated visitor
 * get sent": the server guard (`requireAuth`) calls it on every protected route,
 * so an auth redirect always keeps the visitor on the brand whose domain they're
 * already on:
 *
 *   clawinstall.ai        → /clawinstall/login
 *   flirtmachine.app / *  → /flirtmachine/login   (the default)
 *
 * The port is stripped and the host lower-cased before lookup; an empty or
 * unknown host defaults to the FlirtMachine login.
 *
 * @since 1.0.0
 */
export const resolveLoginPath = (host: string | null | undefined): string => {
  const hostname = (host ?? "").split(":")[0].toLowerCase();
  const prefix = getRoutePrefixForHostname(hostname);
  return prefix && LOGIN_ROUTE_PREFIXES.has(prefix) ? `${prefix}/login` : "/flirtmachine/login";
};
